import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useDebounce } from "use-debounce";
import { UnitService, UnitVM, AccessLevelEnum } from "@propertelligent/client-api";
import { infiniteScrollHook } from "../../hooks/infiniteScrollHook";
import UnitTopHeader from "../../newComponents/listings/top-listing-headers/unit-listing-top-header";
import UnitCard from "../../newComponents/listings/listing-cards/unit";
import AddUnitForm from "../../newComponents/unit/AddUnitForm";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import UnitDetails2 from "../../newComponents/unit/UnitDetails";
import apiHelper from "../../utils/apiHelper";
import { usePermissions } from "../../hooks/usePermissions";
import NoAccess from "../NoAccess";
import { PermissionGuard } from "../../newComponents/PermissionGuard";
import { UserLoginData } from "../../components/common/charts/Enums";

interface UnitsProps {
  isEntity?: boolean;
  entityTypeId?: number;
  entityId?: number;
  className?: string;
  formId?: string;
  onUnitAdded?: () => void;
  unitRefreshTrigger?: number;
}

const Units = ({
  isEntity,
  entityTypeId,
  entityId,
  onUnitAdded,
  unitRefreshTrigger,
}: UnitsProps) => {
  const { hasAccess, canWrite } = usePermissions();
  if (!hasAccess("Unit", 1)) return <NoAccess />;

  const [searchTerm, setSearchTerm] = useState("");
  const [fullyQualifiedFilter, setFullyQualifiedFilter] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [activeFilter, setActiveFilter] = useState("All");
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<UnitVM | undefined>(undefined);
  const [hasAnyUnits, setHasAnyUnits] = useState(false);

  const [tabCounts, setTabCounts] = useState<Record<string, number>>({
    All: 0,
    Vacant: 0,
    Occupied: 0,
  });

  const activeFilterRef = useRef(activeFilter);
  useEffect(() => { activeFilterRef.current = activeFilter; }, [activeFilter]);

  const location = useLocation();
  const navigate = useNavigate();

  const buildQFilter = useCallback((filter: string, search: string): string | null => {
    const parts: string[] = [];
    if (filter === "Vacant") parts.push("!Tenants.Any()");
    if (filter === "Occupied") parts.push("Tenants.Any()");
    if (search) parts.push(`UnitNumber.ToLower().Contains("${search.toLowerCase()}")`);
    return parts.length ? parts.join(" && ") : null;
  }, []);

  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

  const debouncedSearchRef = useRef(debouncedSearchTerm);
  useEffect(() => { debouncedSearchRef.current = debouncedSearchTerm; }, [debouncedSearchTerm]);

  const getUnitListFn = useCallback(() => {
    if (isEntity && entityTypeId !== undefined && entityId !== undefined && entityTypeId === 6) {
      return (
        _: number,
        pageSize?: number, pageNumber?: number, skipValue?: number,
        filterEntityTypeId?: any, filterEntityIds?: any,
        fullyQualifiedFilters?: string, fullyQualifiedSorts?: string,
        ...rest: any[]
      ) =>
        UnitService.getApiUnitPropertylist(
          entityId, pageSize, pageNumber, skipValue,
          filterEntityTypeId, filterEntityIds,
          fullyQualifiedFilters, fullyQualifiedSorts, ...rest
        );
    }
    return UnitService.getApiUnitOrganizationlist;
  }, [isEntity, entityTypeId, entityId]);

  const { rows, loading, hasMore, fetchData, currentPage, aggregateValues, rowCount } =
    infiniteScrollHook({
      httpFetchDataFn: getUnitListFn(),
      FullyQualifiedFilters: fullyQualifiedFilter,
      pageSize: 10,
      FullyQualifiedSorts: `UnitNumber ${sortDirection}`,
    });

  useEffect(() => {
    setFullyQualifiedFilter(buildQFilter(activeFilter, debouncedSearchTerm));
  }, [activeFilter, debouncedSearchTerm, buildQFilter]);

  const searchOnlyFilter = useMemo(() => {
    return debouncedSearchTerm ? `UnitNumber.ToLower().Contains("${debouncedSearchTerm.toLowerCase()}")` : null;
  }, [debouncedSearchTerm]);

  const fetchTabCounts = useCallback(async () => {
    try {
      const orgIdStr = sessionStorage.getItem(UserLoginData.organization_id);
      const tenantIdStr = sessionStorage.getItem(UserLoginData.tenant_id);
      const orgId = orgIdStr ? parseInt(orgIdStr) : 0;
      const tenantId = tenantIdStr ? parseInt(tenantIdStr) : 0;
      const reqID = orgId ? orgId : tenantId;

      const fetchFn = getUnitListFn();
      const res = await apiHelper(
        fetchFn,
        { showNotification: false },
        reqID,
        1, // pageSize: 1
        1, // pageNumber: 1
        undefined, // skipValue
        undefined, // filterEntityTypeId
        undefined, // filterEntityIds
        searchOnlyFilter, // fullyQualifiedFilters (Search ONLY)
        undefined // fullyQualifiedSorts
      );

      if (res?.data) {
        const raw = (res.data.aggregateValues ?? {}) as Record<string, number>;
        const allCount = res.data.recordCount ?? Object.values(raw).reduce((a, b) => a + b, 0);

        setTabCounts({
          All: allCount,
          Vacant: raw.Vacant ?? 0,
          Occupied: raw.Occupied ?? 0,
        });

        if (!searchOnlyFilter) {
          setHasAnyUnits(allCount > 0);
        }
      }
    } catch (err) {
      console.error("Failed to fetch unit tab counts", err);
    }
  }, [getUnitListFn, searchOnlyFilter]);

  useEffect(() => {
    fetchTabCounts();
  }, [fetchTabCounts, unitRefreshTrigger]);

  useEffect(() => {
    if (location.state?.refreshUnits) {
      fetchData(1);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.refreshUnits]);

  useEffect(() => {
    if (unitRefreshTrigger !== undefined && unitRefreshTrigger > 0) fetchData(1);
  }, [unitRefreshTrigger]);

  const handleSort = (dir: "asc" | "desc") => setSortDirection(dir);
  const handleFilterChange = (filter: string) => setActiveFilter(filter);

  const fetchUnitForEdit = async (unitId: number) => {
    try {
      const res = await apiHelper(UnitService.getApiUnit1, { showNotification: false }, unitId);
      if (res?.data?.success) { setSelectedUnit(res.data.success); setModalOpen(true); }
    } catch (err) { console.error(err); }
  };

  const handleModalClose = () => { setModalOpen(false); setSelectedUnit(undefined); };

  const handleUnitMutated = useCallback(() => {
    fetchData(1);
    fetchTabCounts();
    if (onUnitAdded) onUnitAdded();
  }, [fetchData, fetchTabCounts, onUnitAdded]);

  return (
    <Routes>
      <Route
        index
        element={
          <PermissionGuard permission="Unit" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
            <div className="content-wrapper container-fluid">
              {hasAnyUnits && (
                <UnitTopHeader
                  PageHeading="Units"
                  ButtonName="Add Unit"
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  handleSort={handleSort}
                  currentSortDirection={sortDirection}
                  handleFilterChange={handleFilterChange}
                  activeFilter={activeFilter}
                  tabCounts={tabCounts}
                  onClick={() => { setSelectedUnit(undefined); setModalOpen(true); }}
                  showAddButton={canWrite("Unit")}
                />
              )}
              <UnitCard
                Id="unit"
                className="no-side-margin"
                IsFooter={true}
                hasAnyData={hasAnyUnits}
                setModalOpen={setModalOpen}
                dataHookState={{ rows, loading, hasMore, fetchData, currentPage, onRefresh: handleUnitMutated }}
                onEditUnit={(unit) => fetchUnitForEdit(unit.id)}
                activeTab={activeFilter}
              />
              {isModalOpen && (
                <AddUnitForm
                  isModalOpen={isModalOpen}
                  setModalOpen={handleModalClose}
                  fetchData={handleUnitMutated}
                  unitData={selectedUnit}
                  isProperty={isEntity}
                  propertyId={entityId}
                />
              )}
            </div>
          </PermissionGuard>
        }
      />
      <Route
        path="/unitdetails/:id"
        element={
          <PermissionGuard permission="Unit" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
            <UnitDetails2 />
          </PermissionGuard>
        }
      />
    </Routes>
  );
};

export default Units;